import { useLingui } from "@lingui/react/macro";
import { radioCardStyles } from "@offstreetparking/design-system/forms";
import { ChevronRightIcon } from "@offstreetparking/design-system/icons/ChevronRightIcon";
import { TenantIcon } from "@offstreetparking/design-system/icons/TenantIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { classNames } from "@offstreetparking/design-system/utils";
import Image from "next/image";
import { useEffect, useRef } from "react";
import type { TenantInfo } from "../CreatePermitWizard/types";
type TenantSelectionProps = {
	tenants: Pick<TenantInfo, "id" | "name" | "brandingFileUrl">[] | undefined;
	onChange: (tenantId: number) => void;
};
export const TenantSelection = ({ tenants = [], onChange }: TenantSelectionProps) => {
	const firstButtonRef = useRef<HTMLButtonElement>(null);
	const sectionRef = useRef<HTMLElement>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-trigger focus when tenants arrive
	useEffect(() => {
		if (!firstButtonRef.current) {
			return;
		}
		if (typeof document === "undefined") {
			return;
		}
		const active = document.activeElement;
		const isInsideSection = sectionRef.current?.contains(active);
		if (!isInsideSection) {
			firstButtonRef.current.focus();
		}
	}, [tenants]);
	const { t } = useLingui();
	return (
		<section ref={sectionRef} aria-labelledby="tenant-selection-heading" className="space-y-4">
			<Heading id="tenant-selection-heading" className="mt-0.5 flex items-center gap-2">
				<TenantIcon className="h-5 w-5" />
				{t`Who are you visiting?`}
			</Heading>

			{tenants.length === 0 && (
				<Text size="sm" intent="muted">
					{t`There are no tenants available at this location.`}
				</Text>
			)}

			<ul className="flex flex-col gap-2">
				{tenants.map((tenant, index) => (
					<li key={tenant.id}>
						<button
							ref={index === 0 ? firstButtonRef : undefined}
							data-testid={tenant.id}
							aria-label={`${t`Who are you visiting?`}: ${tenant.name}`}
							onClick={() => onChange(tenant.id)}
							type="button"
							className={classNames(
								radioCardStyles({
									checked: false,
								}),
								"flex w-full cursor-pointer flex-row items-center px-4 py-3 hover:border-primary",
							)}
						>
							<div className="flex flex-1 flex-col gap-1 text-left">
								<Text As="span" weight="bold">
									{tenant.name}
								</Text>
							</div>

							{tenant.brandingFileUrl && (
								<div
									className="relative h-[clamp(25px,10vh,50px)] w-[clamp(100px,10vw,200px)]"
									aria-hidden="true"
								>
									<Image
										src={tenant.brandingFileUrl}
										alt=""
										fill
										sizes="(max-width: 1000px) 100px, (max-width: 2000px) 10vw, 200px"
										className="object-contain"
									/>
								</div>
							)}

							<ChevronRightIcon className="h-6 w-6 text-primary" aria-hidden="true" />
						</button>
					</li>
				))}
			</ul>
		</section>
	);
};
